import React from 'react';
import { Root } from './components/Root';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { SparrowTemplateCard, isListedTemplate } from './components/scaffolder/SparrowTemplateCard';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { AppNamePickerFieldExtension } from './components/scaffolder/extensions';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { catalogColumnsFunc } from './components/catalog/catalogColumns';
import { RememberCatalogFilters } from './components/catalog/RememberCatalogFilters';
import { searchPage } from './components/search/SearchPage';

import {
  AlertDisplay,
  OAuthRequestDialog,
} from '@backstage/core-components';
import { CustomSignInPage } from './components/signin/SignInPage';
import { CssBaseline } from '@material-ui/core';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { RequestsPage } from '@internal/backstage-plugin-requests';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { UnifiedThemeProvider } from '@backstage/theme';
import { launchpadLightTheme, launchpadDarkTheme } from './theme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { CostInsightsPage } from '@backstage-community/plugin-cost-insights';
import { 
  SelfServicePage, 
  JenkinsAccess, 
  AwsAccess, 
  AzureAccess, 
  GcpAccess, 
  ClickhouseAccess, 
  PostgresAccess, 
  OpensearchAccess 
} from '@internal/backstage-plugin-self-service';
import { SparrowInfraPilotPage } from '@internal/backstage-plugin-sparrow-infra-pilot';
import { applyDefaultThemePreference } from './theme/applyDefaultThemePreference';
import { DARK_THEME_ID, LIGHT_THEME_ID } from './theme/themeIds';

// First-time visitors default to Light regardless of their OS appearance.
// Must run before createApp() reads the persisted theme selection.
applyDefaultThemePreference();

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => <CustomSignInPage {...props} />,
  },
  themes: [
    {
      id: LIGHT_THEME_ID,
      title: 'LaunchPad Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={launchpadLightTheme}>
          <CssBaseline>{children}</CssBaseline>
        </UnifiedThemeProvider>
      ),
    },
    {
      id: DARK_THEME_ID,
      title: 'LaunchPad Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={launchpadDarkTheme}>
          <CssBaseline>{children}</CssBaseline>
        </UnifiedThemeProvider>
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route
      path="/catalog"
      element={
        <RememberCatalogFilters>
          <CatalogIndexPage columns={catalogColumnsFunc} />
        </RememberCatalogFilters>
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route 
      path="/create" 
      element={
        <ScaffolderPage 
          components={{ TemplateCardComponent: SparrowTemplateCard }}
          templateFilter={isListedTemplate}
        />
      } 
    >
      <ScaffolderFieldExtensions>
        <AppNamePickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/requests" element={<RequestsPage />} />
    <Route path="/rbac" element={<RbacPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/cost-insights" element={<CostInsightsPage />} />
    <Route path="/self-service" element={<SelfServicePage />} />
    <Route path="/self-service/jenkins" element={<JenkinsAccess />} />
    <Route path="/self-service/aws" element={<AwsAccess />} />
    <Route path="/self-service/azure" element={<AzureAccess />} />
    <Route path="/self-service/gcp" element={<GcpAccess />} />
    <Route path="/self-service/clickhouse" element={<ClickhouseAccess />} />
    <Route path="/self-service/postgres" element={<PostgresAccess />} />
    <Route path="/self-service/opensearch" element={<OpensearchAccess />} />
    <Route path="/sip" element={<SparrowInfraPilotPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
